import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import slugify from "slugify"

import { STATES_MAPPER } from "configs/country-state-names"
import { RootState } from "stores/store"
import { markStates } from "utils/misc"
import { slugifyForLocationName } from "utils/url"

import { LocalSearchInitialState } from "./types"

export const initialState: LocalSearchInitialState = {
  locationData: {
    bounds: null,
    shortName: "",
    placeId: null,
    lat: null,
    lng: null,
    longName: null,
    placeType: "",
    Location: [],
    isSearchingInMap: false,
    propertyConfigTypeId: null,
    page: 0,
  },
  dateRangeData: {
    endDate: null,
    startDate: null,
    noOfDays: 0,
  },
  guestDetails: {
    adultCount: 2,
    childrenCount: 0,
    petAllowed: false,
    childAllowed: false,
    suitableChild: false,
    numberOfGuest: 0,
  },
  newSearchURL: "/",
  searchAddressResult: null,
}

export const localSearchSlice = createSlice({
  name: "localSearch",
  initialState,
  reducers: {
    resetLocalSearch: (state) => {
      markStates(initialState, state)
    },
    markLocalSearch: (
      state,
      action: PayloadAction<Partial<LocalSearchInitialState>>,
    ) => {
      markStates(action.payload, state)

      let slugName =
        state?.locationData.shortName || state?.locationData.longName || "USA" // default should be USA
      const tempArr = slugName?.split(",")
      if (tempArr?.length === 3) {
        // ["city","state","country"]
        tempArr[1] = ` ${
          (STATES_MAPPER[tempArr[1]?.trim()] as string) || tempArr[1]?.trim()
        }`
        slugName = tempArr?.join()
      }

      const query: Record<string, string> = {}

      const adultCount = state?.guestDetails.adultCount
      if (adultCount) query.adultCount = adultCount?.toString()

      const bounds = state?.locationData.bounds || ""
      if (bounds) query.bounds = bounds

      const childAllowed =
        state?.guestDetails.childAllowed ||
        (!!state?.guestDetails.childrenCount &&
          state?.guestDetails.childrenCount > 0)
      if (childAllowed) query.childAllowed = "true"

      const childrenCount = state?.guestDetails.childrenCount
      if (childrenCount) query.childrenCount = childrenCount?.toString()

      const endDate = state?.dateRangeData.endDate || ""
      if (endDate) query.endDate = endDate

      const lat = state?.locationData.lat?.toString()
      if (lat && lat !== "32.7749295") query.lat = lat

      const lng = state?.locationData.lng?.toString()
      if (lng && lng !== "-122.4194155") query.lng = lng

      const name = encodeURIComponent(
        state?.locationData.shortName || state?.locationData.longName || "",
      )
      if (name) query.name = name

      const noOfDays = state?.dateRangeData.noOfDays
      if (noOfDays) query.noOfDays = noOfDays?.toString()

      const numberOfGuest = state?.guestDetails?.numberOfGuest
      if (numberOfGuest) query.numberOfGuest = numberOfGuest?.toString()

      const petAllowed = state?.guestDetails.petAllowed
      if (petAllowed) query.petAllowed = "true"

      const placeDesc = slugifyForLocationName(
        state?.locationData.longName || "",
      )
      if (placeDesc) query.placeDesc = placeDesc

      const placeId = state?.locationData.placeId || ""
      if (placeId) query.placeId = placeId

      const placeType = state?.locationData.placeType || ""
      if (placeType) query.placeType = placeType

      const propertyConfigTypeId =
        state?.locationData.propertyConfigTypeId?.toString()
      if (propertyConfigTypeId)
        query.propertyConfigTypeId = propertyConfigTypeId

      const startDate = state?.dateRangeData.startDate
      if (startDate) query.startDate = startDate

      const queryString = Object.entries(query)
        .map(([k, v]) => `${k}=${v}`)
        .join("&")

      const newURL = `/vacation-rentals/${slugify(
        slugName.replace(/,/g, "-").replace(/ /g, "-"),
        { lower: false, replacement: "_" },
      )}?${queryString}`

      state.newSearchURL = newURL
    },
  },
})

export const { markLocalSearch, resetLocalSearch } = localSearchSlice.actions

export const selectLocalSearch = (state: RootState) => state.localSearchReducer

export const selectLocalSearchLocation = (state: RootState) =>
  state.localSearchReducer.locationData

export const selectLocalSearchDateRange = (state: RootState) =>
  state.localSearchReducer.dateRangeData

export const selectLocalSearchGuests = (state: RootState) =>
  state.localSearchReducer.guestDetails
export default localSearchSlice.reducer
